import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";

export interface CategoryChild {
  id: number;
  child_id: number;
  name: string;
}

export interface Category {
  id: number;
  name: string;
  children: CategoryChild[];
}

export interface CategoriesResponse {
  status: boolean;
  message: string;
  data: Category[];
}

// Fetch all parent categories and their children
export const useCategories = () => {
  return useQuery<CategoriesResponse>({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await apiClient.get<CategoriesResponse>(
        "/category/all/parents"
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
};
// Create a new category or subcategory
export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryData: {
      name: string;
      parent_id?: number;
      image?: string;
    }) => {
      const response = await apiClient.post("/category/create", categoryData);
      return response.data;
    },
    onSuccess: () => {
      message.success("Category created successfully!");
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: () => {
      message.error("Failed to create category. Please try again.");
    },
  });
};

// Update an existing category or subcategory
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryData: {
      id: number;
      name: string;
      image?: string;
    }) => {
      const response = await apiClient.put(`/category/${categoryData.id}`, {
        name: categoryData.name,
        image: categoryData.image,
      });
      return response.data;
    },
    onSuccess: () => {
      message.success("Category updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: () => {
      message.error("Failed to update category. Please try again.");
    },
  });
};

// Delete a category
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryId: number) => {
      // Step 1: Delete the category
      const deleteResponse = await apiClient.delete(`/category/${categoryId}`);

      return deleteResponse.data;
    },
    onSuccess: () => {
      message.success("Category deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["categories"] }); // Refresh the categories list
    },
    onError: () => {
      message.error("Failed to delete category. Please try again.");
    },
  });
};
